
import React, { Component } from 'react';
import { HTTPService } from "./../services/httpService";
import JOI from 'joi-browser';
import {
    ASSIGNMENT_TYPES,
    getUnifiedRolesForType,
    isValidUnifiedRole,
    getUnifiedRoleBadgeColor,
    getUnifiedRoleLabel
} from "./../util/staffRoles";

require('dotenv').config();

const API = new HTTPService();

export default class Users extends Component {

    constructor(props) {
        super(props);
        this._isMounted = true;
    }

    state = {
        // View mode: 'list', 'create', 'edit'
        viewMode: 'list',

        // User data for create/edit forms
        data: {
            email: "",
            name: "",
            phone: "",
            user_type: "",           // 'dealer' or 'installer' (matches user_companies.company_type)
            id: "",
            primary_company_id: "", // installer_id for installer, group_id for dealer
            assignment_type: "",     // 'dealer' or 'installer' — same enum as user_type
            scoped_id: null,         // user_companies.location_id (0/null = all, > 0 = pinned to one location/dealer)
            role: "",                // Unified role: admin, manager, technician, viewer
        },
        errors: {},

        // Lists
        userList: [],
        dealerList: [],
        specialistList: [],
        dealerGroupList: [],         // NEW: List of dealer groups
        availableCompanies: [],

        // Multi-company associations
        userCompanies: [],           // Additional companies (excluding primary)
        allUserCompanies: [],        // All companies user has access to (from API)

        // NEW: Locations for scoping (populated when group/company selected)
        scopeLocations: [],          // Dealers in selected group OR locations in selected installer

        // Search and filtering
        searchTerm: "",
        roleFilter: "",
        userTypeFilter: "",
        filteredUsers: [],

        // Cognito status
        cognitoAccountStatus: null,
        isCheckingCognito: false
    };

    async componentDidMount() {            
        await this.loadUsers();
        await this.loadCompanies();
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    // Load all users for the list view
    async loadUsers() {
        try {
            const userRequest = "/users";
            const { data: users } = await API.get(userRequest);
            this.setState({
                userList: users,
                filteredUsers: users
            });
            //console.log("loadUsers: User List ", users);
        } catch (error) {
            console.error("Error loading users:", error);
        }
    }

    // Load available companies
    async loadCompanies() {
        try {
            // Load dealerships
            const dealerRequest = "/dealers";
            const { data: dealers } = await API.get(dealerRequest);
            dealers.forEach(function (element) {
                element.company_type = "dealer";  // NEW: Use new company type
            });

            // Load dealer groups (the root of all dealer-side access in the new model)
            let dealerGroups = [];
            try {
                const { data: groups } = await API.get("/dealer-groups");
                dealerGroups = groups || [];
                dealerGroups.forEach(function (element) {
                    element.company_type = "dealer";
                });
            } catch (err) {
                console.log("Dealer groups endpoint not available:", err.message);
            }

            // Load specialists (installers)
            const specialistRequest = "/installers";
            const { data: specialists } = await API.get(specialistRequest);
            specialists.forEach(function (element) {
                element.company_type = "installer";  // NEW: Use new company type
            });

            // NOTE: Installer locations are loaded on-demand via /installers/:installerId/locations
            // when a specific installer is selected (see loadScopeLocations)

            // Sort lists alphabetically
            dealers.sort((a, b) => (a.dealer_name || '').localeCompare(b.dealer_name || ''));
            dealerGroups.sort((a, b) => (a.group_name || a.name || '').localeCompare(b.group_name || b.name || ''));
            specialists.sort((a, b) => (a.Installer_Name || '').localeCompare(b.Installer_Name || ''));

            const allCompanies = [...dealers, ...dealerGroups, ...specialists];

            this.setState({
                dealerList: dealers,
                dealerGroupList: dealerGroups,
                specialistList: specialists,
                availableCompanies: allCompanies
            });
        } catch (error) {
            console.error("Error loading companies:", error);
        }
    }

    // Load the available sub-scopes for a company.
    //   assignmentType === 'dealer'    → dealerships under the group_id
    //   assignmentType === 'installer' → locations under the installer_id
    // The sub-scope value is later stored in user_companies.location_id
    // (0/null = all; > 0 = pinned to that single dealer_id / location_id).
    async loadScopeLocations(companyId, assignmentType) {
        try {
            let locations = [];

            if (assignmentType === 'dealer') {
                const { data } = await API.get(`/dealer-groups/${companyId}/dealerships`);
                // Don't offer closed/inactive dealerships for new assignments. But
                // when editing a user already pinned to a now-closed dealer, keep
                // that dealer in the list (labeled "(Closed)") so the current
                // assignment renders and isn't silently blanked on save.
                const currentScopedId = this.state.data.scoped_id;
                const isClosed = d =>
                    d.dealer_active === 0 || d.dealer_active === '0' ||
                    d.is_active === false || d.is_active === 0 || d.is_active === '0';
                locations = (data || [])
                    .filter(d => !isClosed(d) || String(d.dealer_id) === String(currentScopedId))
                    .map(d => ({
                        id: d.dealer_id,
                        name: isClosed(d)
                            ? `${d.dealer_name || d.name} (Closed)`
                            : (d.dealer_name || d.name)
                    }));
            } else if (assignmentType === 'installer') {
                const { data } = await API.get(`/installers/${companyId}/locations`);
                locations = (data || []).map(l => {
                    const addressParts = [
                        l.installer_address_line1,
                        l.installer_address_city,
                        l.installer_address_state
                    ].filter(Boolean);
                    const locationName = addressParts.join(', ') || `Location ${l.location_id}`;

                    return {
                        id: l.location_id,
                        name: locationName
                    };
                });
            }

            this.setState({ scopeLocations: locations }, () => {
                // Auto-assign technicians to the only available location
                if (this.state.data.role === 'technician' && locations.length === 1 && assignmentType === 'installer') {
                    const data = { ...this.state.data, scoped_id: locations[0].id };
                    this.setState({ data });
                }
            });
        } catch (error) {
            console.error("Error loading scope locations:", error);
            this.setState({ scopeLocations: [] });
        }
    }

    // Load user's company associations
    async loadUserCompanies(userId) {
        try {
            // NEW: Use the updated endpoint that returns new format
            const userCompaniesRequest = "/user-companies/" + userId;
            const { data: userCompanies } = await API.get(userCompaniesRequest);
            //console.log("Raw user companies from /user-companies:", userCompanies);

            // Process the company data - handle NEW API response format
            let processedCompanies = [];

            if (Array.isArray(userCompanies)) {
                processedCompanies = userCompanies.map(uc => {
                    // API response shape (post-2026-05-15 server refactor):
                    //   { company_type, scoped_id, role, Installer_id, Installer_Name, scoped_location_name }
                    // parent_id is the legacy alias still returned by the server
                    // for backward compat — read both until the alias is dropped.
                    return {
                        company_id: uc.Installer_id || uc.company_id,
                        company_type: uc.company_type,
                        company_name: uc.Installer_Name || uc.company_name,
                        scoped_id: uc.scoped_id ?? uc.parent_id ?? null,
                        role: uc.role || 'viewer',
                        scoped_location_name: uc.scoped_location_name || null
                    };
                }).filter(Boolean);
            }

            //console.log("Processed user companies:", processedCompanies);

            // Determine user type from company types
            const isDealer = processedCompanies.some(uc => uc.company_type === 'dealer');

            // Filter to only show companies of the same type as the user (excluding primary)
            const filteredCompanies = processedCompanies.filter(uc => {
                const matchesUserType = isDealer
                    ? uc.company_type === 'dealer'
                    : uc.company_type === 'installer';
                return matchesUserType && uc.company_id !== this.state.data.primary_company_id;
            });

            //console.log("Filtered additional companies:", filteredCompanies);

            // Preserve existing associations so they're re-sent on save (the
            // additional-company management UI was removed; primary edits only).
            this.setState({
                userCompanies: filteredCompanies,
                allUserCompanies: processedCompanies
            });

        } catch (error) {
            console.error("Error loading user companies:", error);
            // Fallback to old endpoint if new one doesn't exist
            try {
                const fallbackRequest = "/useraccess/" + userId;
                const { data: userCompanies } = await API.get(fallbackRequest);

                let processedCompanies = [];
                if (Array.isArray(userCompanies)) {
                    processedCompanies = userCompanies.map(uc => {
                        if (uc.Installer_id) {
                            return {
                                company_id: uc.Installer_id,
                                company_type: "installer",
                                company_name: uc.Installer_Name,
                                scoped_id: null,
                                role: 'viewer',
                                scoped_location_name: null
                            };
                        }
                        if (uc.dealer_id) {
                            return {
                                company_id: uc.dealer_id,
                                company_type: "dealer",
                                company_name: uc.dealer_name,
                                scoped_id: null,
                                role: 'viewer',
                                scoped_location_name: null
                            };
                        }
                        return null;
                    }).filter(Boolean);
                }

                this.setState({
                    userCompanies: processedCompanies,
                    allUserCompanies: processedCompanies
                });
            } catch (fallbackError) {
                console.error("Fallback also failed:", fallbackError);
                this.setState({
                    userCompanies: [],
                    allUserCompanies: []
                });
            }
        }
    }

    // Check Cognito status
    async checkCognitoStatus(email) {
        try {
            this.setState({ isCheckingCognito: true });
            const statusRequest = "/auth/user-status/" + encodeURIComponent(email);
            const { data: statusResult } = await API.get(statusRequest);
            this.setState({ 
                isCheckingCognito: false, 
                cognitoAccountStatus: statusResult.status
            });
        } catch (error) {
            console.error('Error checking Cognito status:', error);
            this.setState({ 
                isCheckingCognito: false, 
                cognitoAccountStatus: 'error'
            });
        }
    }

    // Filter the user list by search term + role + user type (all AND-combined).
    filterUsers = (searchTerm, roleFilter, userTypeFilter) => {
        const term = (searchTerm || '').toLowerCase();
        return (this.state.userList || []).filter(user => {
            const userType = user.User_type || user.company_type;
            const role = user.role || 'viewer';
            const matchesSearch = !term
                || (user.user_name || '').toLowerCase().includes(term)
                || (user.Email || '').toLowerCase().includes(term);
            // Case-insensitive: the /users list can return raw stored casing
            // (e.g. 'Finance'/'ADMIN') while the filter values are lowercase.
            const matchesRole = !roleFilter || role.toLowerCase() === roleFilter.toLowerCase();
            const matchesType = !userTypeFilter || (userType || '').toLowerCase() === userTypeFilter.toLowerCase();
            return matchesSearch && matchesRole && matchesType;
        });
    };

    handleSearch = (event) => {
        const searchTerm = event.target.value;
        this.setState({
            searchTerm,
            filteredUsers: this.filterUsers(searchTerm, this.state.roleFilter, this.state.userTypeFilter)
        });
    };

    handleRoleFilter = (event) => {
        const roleFilter = event.target.value;
        this.setState({
            roleFilter,
            filteredUsers: this.filterUsers(this.state.searchTerm, roleFilter, this.state.userTypeFilter)
        });
    };

    handleUserTypeFilter = (event) => {
        const userTypeFilter = event.target.value;
        // Role options depend on user type — reset role filter when type changes.
        const roleFilter = "";
        this.setState({
            userTypeFilter,
            roleFilter,
            filteredUsers: this.filterUsers(this.state.searchTerm, roleFilter, userTypeFilter)
        });
    };

    // View mode handlers
    handleAddUser = () => {
        this.setState({
            viewMode: 'create',
            data: {
                email: "",
                name: "",
                phone: "",
                user_type: "",
                id: "",
                primary_company_id: "",
                assignment_type: "",
                scoped_id: null,
                role: "",
            },
            errors: {},
            userCompanies: [],
            allUserCompanies: [],
            scopeLocations: [],
            cognitoAccountStatus: null
        });
    };

    handleEditUser = async (user) => {
        // user_companies.company_type is one of 'installer' | 'dealer'.
        // Same value is reused as assignment_type in the form.
        let userType = "";
        if (user.User_type === "dealer") {
            userType = "dealer";
        } else if (user.User_type === "installer" || user.User_type === "specialist") {
            userType = "installer";
        } else {
            // Fallback: determine from primary company if User_type is unclear
            const company = this.state.availableCompanies.find(c =>
                (c.dealer_id && c.dealer_id === user.Company_id) ||
                (c.Installer_id && c.Installer_id === user.Company_id)
            );
            if (company) {
                userType = company.company_type === "dealer" ? "dealer" : "installer";
            }
        }

        // The Company picker is the parent entity: dealer GROUP for dealers,
        // installer for installers. /users returns users.Company_id = installer_id
        // for installers but a dealer_id for dealers, plus group_id (the dealer's
        // dealer_group_id). Use the group for dealers; the dealer_id itself is the
        // scope (scoped_id). Fall back to Company_id if group_id is absent (legacy
        // server) — dealer pickers stay blank in that case, same as before.
        const primaryCompanyId = userType === 'dealer'
            ? (user.group_id ?? user.Company_id)
            : user.Company_id;

        const userData = {
            email: user.Email,
            name: user.user_name,
            phone: user.user_phone_number,
            user_type: userType,
            id: user.User_id,
            primary_company_id: primaryCompanyId,
            assignment_type: userType,
            // Server returns scoped_id (canonical) and parent_id (legacy alias).
            // Read both until the alias is dropped.
            scoped_id: user.scoped_id ?? user.parent_id ?? null,
            role: user.role || 'viewer',
        };

        this.setState({
            viewMode: 'edit',
            data: userData,
            errors: {},
            cognitoAccountStatus: null,
            userCompanies: [],
            allUserCompanies: [],
            scopeLocations: []
        });

        await this.loadUserCompanies(user.User_id);

        // Pre-load the sub-scope picker so an "edit" can change scope without
        // first re-selecting the parent. Use the resolved parent id (group for
        // dealers) — loadScopeLocations expects a group_id for the dealer branch.
        if (userType && primaryCompanyId) {
            await this.loadScopeLocations(primaryCompanyId, userType);
        }

        if (user.Email) {
            await this.checkCognitoStatus(user.Email);
        }
    };

    handleBackToList = () => {
        this.setState({ viewMode: 'list' });
    };

    handleAssignmentTypeChange = async (event) => {
        const assignmentType = event.target.value;
        const data = { ...this.state.data };
        data.assignment_type = assignmentType;
        data.user_type = assignmentType;       // mirrors user_companies.company_type
        data.primary_company_id = "";
        data.scoped_id = null;
        data.role = "";                         // role options depend on company type — reset
        this.setState({ data, scopeLocations: [] });
    };

    // Handle parent company / dealer group selection.
    // For 'installer', companyId = installer_id (Installer_Name list)
    // For 'dealer',    companyId = group_id (dealer group list)
    handleCompanySelectionChange = async (event) => {
        const companyId = event.target.value;
        const data = { ...this.state.data };
        data.primary_company_id = companyId;
        data.scoped_id = null;  // Reset sub-scope when parent changes
        this.setState({ data });

        if (companyId && data.assignment_type) {
            await this.loadScopeLocations(companyId, data.assignment_type);
        } else {
            this.setState({ scopeLocations: [] });
        }
    };

    handleScopeLocationChange = (event) => {
        const scopedId = event.target.value || null;
        const data = { ...this.state.data };
        data.scoped_id = scopedId;
        this.setState({ data });
    };

    // NEW: Handle role change
    handleRoleChange = (event) => {
        const role = event.target.value;
        const data = { ...this.state.data };
        data.role = role;

        // Technicians must be pinned to one location — auto-assign when the
        // installer only has a single location to pick from.
        if (role === 'technician' && data.assignment_type === 'installer' && this.state.scopeLocations.length === 1) {
            data.scoped_id = this.state.scopeLocations[0].id;
        }

        this.setState({ data });
    };

    // Handle primary company selection
    handlePrimaryCompanyChange = (event) => {
        const companyId = event.target.value;
        
        const data = { ...this.state.data };
        data.primary_company_id = companyId;
        
        this.setState({ data });
    };

    // Handle company selection for new users
    handleNewUserCompanyChange = (event) => {
        const companyId = event.target.value;
        const company = this.state.availableCompanies.find(c => 
            (c.dealer_id && c.dealer_id === companyId) || 
            (c.Installer_id && c.Installer_id === companyId)
        );
        
        if (company) {
            const data = { ...this.state.data };
            data.primary_company_id = companyId;
            data.company_type = company.company_type;
            this.setState({ data });
        }
    };

    // Form validation - NEW: Updated for new assignment/role system
    userSchema = {
        email: JOI.string().email().required().label('email'),
        name: JOI.string().required().label('name'),
        phone: JOI.string().regex(/^[0-9]{10}$/).required().label('phone'),
        primary_company_id: JOI.alternatives().try(
            JOI.string().required(),
            JOI.number().required()
        ).label('company'),
        assignment_type: JOI.string().valid('dealer', 'installer').required().label('assignment type'),
        role: JOI.string().valid('owner', 'admin', 'manager', 'technician', 'viewer', 'finance', 'reporting', 'contact').required().label('role'),
        scoped_id: JOI.optional().allow(null, ''),
        user_type: JOI.optional(),
        id: JOI.optional(),
    };

    validate = () => {
        const options = { abortEarly: false };
        const { error } = JOI.validate(this.state.data, this.userSchema, options);

        const errors = {};
        if (error) {
            for (let item of error.details) errors[item.path[0]] = item.message;
        }

        // Role must be valid for the chosen company type (installer vs dealer)
        const { role, assignment_type } = this.state.data;
        if (role && assignment_type && !isValidUnifiedRole(role, assignment_type)) {
            errors.role = `That role isn't available for ${assignment_type === 'dealer' ? 'a dealer' : 'an installer'}`;
        }

        // Dealer users must be assigned to a specific dealership (this page is
        // for single-dealership in-field users; group-wide access is handled
        // by the separate internal flow).
        if (assignment_type === 'dealer' && !this.state.data.scoped_id) {
            errors.scoped_id = 'Select a dealership for this user';
        }

        // Technicians must be scoped to a location
        if (role === 'technician') {
            const { scoped_id } = this.state.data;
            if (assignment_type === 'installer' && !scoped_id) {
                errors.scoped_id = 'Technicians must be assigned to a specific location';
            }
        }

        if (Object.keys(errors).length > 0) {
            this.setState({ errors });
            return false;
        }
        return true;
    };

    validateProperty = ({ name, value }) => {
        const obj = { [name]: value };
        const schema = { [name]: this.userSchema[name] };
        const { error } = JOI.validate(obj, schema);
        return error ? error.details[0].message : null;
    };
      
    handleDataChange = ({ currentTarget: input }) => {
        const errors = { ...this.state.errors };
        const errorMessage = this.validateProperty(input);
        if (errorMessage) errors[input.name] = errorMessage;
        else delete errors[input.name];
    
        const data = { ...this.state.data };
        data[input.name] = input.value;
    
        this.setState({ data, errors });
    };

    // Save user (create or update)
    handleSave = async () => {
        if (!this.validate()) return;

        try {
            // NEW: Updated payload format
            const userData = {
                email: this.state.data.email,
                userName: this.state.data.name,
                userPhoneNumber: this.state.data.phone,
                userType: this.state.data.user_type,  // 'dealer' or 'installer'
                companyId: this.state.data.primary_company_id,
                // The specific dealership/location. For dealers companyId is the
                // GROUP, so the server needs location_id to pin users.Company_id to
                // the chosen dealership (else it falls back to the group's lowest
                // dealer and the user won't appear under that dealership's staff).
                location_id: this.state.data.scoped_id || null,
                role: this.state.data.role,            // 'admin', 'manager', 'technician', 'viewer'
                // User Management always operates at the user level — every user
                // created here gets a system login + invite.
                createLogin: true,
                active: 1
            };

            let userId;
            if (this.state.viewMode === 'create') {
                const { data: result } = await API.post("/users", userData);
                userId = result.userId;
                alert(result.warning
                    ? "User created, but the login could not be set up: " + result.warning
                    : "User created! They'll receive an email to set up their login.");
            } else {
                userData.userId = this.state.data.id;
                await API.put("/users/" + this.state.data.id, userData);
                userId = this.state.data.id;
                alert("User updated successfully!");
            }

            // Save company associations with new format
            await this.saveUserCompanyAssociations(userId);

            // Reload users and return to list
            await this.loadUsers();
            this.setState({ viewMode: 'list' });

        } catch (error) {
            console.error('Error saving user:', error);
            alert("Error saving user: " + (error.response?.data?.message || error.message));
        }
    };

    // Save user-company associations - NEW: Updated payload format
    saveUserCompanyAssociations = async (userId) => {
        try {
            // Send associations with company_type, scoped_id, and role.
            // Server-side canonical field is scoped_id (2026-05-15 refactor).
            // We also send parent_id with the same value as a deprecated alias
            // so older server builds that still expect parent_id keep working.
            // Drop parent_id from the payload once server has dropped the alias.
            const associations = [
                {
                    user_id: userId,
                    company_id: this.state.data.primary_company_id,
                    company_type: this.state.data.assignment_type,
                    scoped_id: this.state.data.scoped_id || null,
                    parent_id: this.state.data.scoped_id || null,  // legacy alias
                    role: this.state.data.role
                },
                ...this.state.userCompanies.map(uc => ({
                    user_id: userId,
                    company_id: uc.company_id,
                    company_type: uc.company_type,
                    scoped_id: uc.scoped_id || null,
                    parent_id: uc.scoped_id || null,  // legacy alias
                    role: uc.role || 'viewer'
                }))
            ];

            // NEW: Use updated endpoint
            await API.post("/user-companies/" + userId, { associations });
        } catch (error) {
            console.error('Error saving company associations:', error);
            // Fallback to old endpoint
            try {
                const oldAssociations = [
                    {
                        company_id: this.state.data.primary_company_id,
                        company_type: this.state.data.assignment_type
                    },
                    ...this.state.userCompanies.map(uc => ({
                        company_id: uc.company_id,
                        company_type: uc.company_type
                    }))
                ];
                await API.post("/useraccess/" + userId, { associations: oldAssociations });
            } catch (fallbackError) {
                console.error('Fallback save also failed:', fallbackError);
            }
        }
    };

    // Password reset
    handlePasswordReset = async () => {
        try {
            const resetData = { 
                email: this.state.data.email
            };
            await API.post("/auth/request-password-reset", resetData);
            alert("Password reset email sent successfully!");
            await this.checkCognitoStatus(this.state.data.email);
        } catch (error) {
            alert("Error sending password reset: " + (error.response?.data?.message || error.message));
        }
    };

    // Get Cognito status display
    getCognitoStatusDisplay = () => {
        if (this.state.isCheckingCognito) {
            return { text: "Checking account status...", color: "#6c757d", bgColor: "#f8f9fa" };
        }
        
        switch (this.state.cognitoAccountStatus) {
            case 'active':
                return { text: "✅ Cognito Account Active", color: "#006600", bgColor: "#e6ffe6" };
            case 'pending':
                return { text: "⏳ Cognito Account Pending", color: "#cc6600", bgColor: "#fff3e0" };
            case 'disabled':
                return { text: "⚠️ Cognito Account Disabled", color: "#cc0000", bgColor: "#ffe6e6" };
            case 'not_found':
                return { text: "❌ No Cognito Account Found", color: "#cc0000", bgColor: "#ffe6e6" };
            case 'error':
                return { text: "❌ Error checking Cognito status", color: "#cc0000", bgColor: "#ffe6e6" };
            default:
                return null;
        }
    };

    // Render methods
    renderUserList = () => {
        // Role filter options follow the selected user-type filter; with no type
        // selected, show the deduped union of installer + dealer roles.
        const roleFilterOptions = this.state.userTypeFilter
            ? getUnifiedRolesForType(this.state.userTypeFilter)
            : [...getUnifiedRolesForType('installer'), ...getUnifiedRolesForType('dealer')]
                .filter((r, i, arr) => arr.findIndex(x => x.value === r.value) === i);
        return (
            <div>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4>User Management</h4>
                    <button
                        className="jv-btn jv-btn-primary"
                        onClick={this.handleAddUser}
                    >
                        <i className="fa-sharp fa-regular fa-plus"></i> Add New User
                    </button>
                </div>

                {/* Search + filters */}
                <div className="d-flex flex-wrap mb-3" style={{ gap: "0.75rem" }}>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search users by name or email..."
                        value={this.state.searchTerm}
                        onChange={this.handleSearch}
                        style={{ maxWidth: "400px" }}
                    />
                    <select
                        className="form-control"
                        value={this.state.userTypeFilter}
                        onChange={this.handleUserTypeFilter}
                        style={{ maxWidth: "200px" }}
                    >
                        <option value="">All User Types</option>
                        <option value="installer">Installer</option>
                        <option value="dealer">Dealer</option>
                    </select>
                    <select
                        className="form-control"
                        value={this.state.roleFilter}
                        onChange={this.handleRoleFilter}
                        style={{ maxWidth: "200px" }}
                    >
                        <option value="">All Roles</option>
                        {roleFilterOptions.map(role => (
                            <option key={role.value} value={role.value}>{role.label}</option>
                        ))}
                    </select>
                </div>

                {/* User Table - NEW: Updated columns for role and scoping */}
                <div className="table-responsive">
                    <table className="jv-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Phone</th>
                                <th>Role</th>
                                <th>User Type</th>
                                <th>Company/Group</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {this.state.filteredUsers.map(user => {
                                // Look up the company name using the canonical 2-value
                                // company_type. For dealer-type rows, Company_id is now
                                // a group_id (dealer-group list); for installer-type
                                // rows it's an installer_id.
                                let companyName = 'Unknown';
                                // Normalize casing — /users can return raw stored values
                                // (e.g. 'Dealer'/'Finance') that won't match lowercase checks.
                                const userType = (user.User_type || user.company_type || '').toLowerCase();

                                if (userType === 'dealer') {
                                    // For dealers, Company_id is the DEALERSHIP (dealer_id) and
                                    // user.group_id is its group. Match the dealership first —
                                    // never compare a dealer_id against group_ids (the id
                                    // namespaces collide, which mislabels the row, e.g. showing
                                    // an unrelated group's name). Fall back to the group via the
                                    // explicit group_id field.
                                    const dealer = this.state.dealerList.find(d => String(d.dealer_id) === String(user.Company_id));
                                    if (dealer) {
                                        companyName = dealer.dealer_name;
                                    } else {
                                        const group = this.state.dealerGroupList.find(g =>
                                            String(g.group_id ?? g.id) === String(user.group_id)
                                        );
                                        if (group) companyName = group.group_name || group.name;
                                    }
                                } else if (userType === 'installer') {
                                    const company = this.state.specialistList.find(s => String(s.Installer_id) === String(user.Company_id));
                                    if (company) companyName = company.Installer_Name;
                                }

                                // Get role info (lowercased so the badge color/label
                                // resolve even when /users returns raw casing like 'Finance').
                                const role = (user.role || 'viewer').toLowerCase();

                                return (
                                    <tr key={user.User_id}>
                                        <td>{user.user_name}</td>
                                        <td>{user.Email}</td>
                                        <td>{user.user_phone_number}</td>
                                        <td>
                                            <span className={`badge badge-${getUnifiedRoleBadgeColor(role)}`}>
                                                {getUnifiedRoleLabel(role)}
                                            </span>
                                        </td>
                                        <td>{userType ? userType.charAt(0).toUpperCase() + userType.slice(1).toLowerCase() : '—'}</td>
                                        <td>{companyName}</td>
                                        <td>
                                            <button
                                                className="jv-btn-icon"
                                                onClick={() => this.handleEditUser(user)}
                                                title="Edit user"
                                            >
                                                <i className="fa-sharp fa-regular fa-pen-to-square"></i>
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    renderUserForm = () => {
        const isEdit = this.state.viewMode === 'edit';

        return (
            <div>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4>{isEdit ? 'Edit User' : 'Create New User'}</h4>
                    <button
                        className="jv-btn jv-btn-tertiary"
                        onClick={this.handleBackToList}
                    >
                        <i className="fa-sharp fa-regular fa-arrow-left"></i> Back to List
                    </button>
                </div>

                <div className="row">
                    <div className="col-md-12">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title">User Information</h5>
                                
                                {/* Cognito Status */}
                                {isEdit && this.getCognitoStatusDisplay() && (
                                    <div className="alert" style={{ 
                                        backgroundColor: this.getCognitoStatusDisplay().bgColor,
                                        color: this.getCognitoStatusDisplay().color,
                                        borderColor: this.getCognitoStatusDisplay().color
                                    }}>
                                        {this.getCognitoStatusDisplay().text}
                                        {!this.state.isCheckingCognito && (
                                            <button
                                                className="btn btn-sm btn-outline-secondary ml-2"
                                                onClick={() => this.checkCognitoStatus(this.state.data.email)}
                                            >
                                                Refresh
                                            </button>
                                        )}
                                    </div>
                                )}

                                {/* Form Fields */}
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label>Full Name *</label>
                                            <input
                                                type="text"
                                                name="name"
                                                className="form-control"
                                                value={this.state.data.name}
                                                onChange={this.handleDataChange}
                                            />
                                            {this.state.errors.name &&
                                                <div className="text-danger">{this.state.errors.name}</div>}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label>Email *</label>
                                            <input
                                                type="email"
                                                name="email"
                                                className="form-control"
                                                value={this.state.data.email}
                                                onChange={this.handleDataChange}
                                            />
                                            {this.state.errors.email &&
                                                <div className="text-danger">{this.state.errors.email}</div>}
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label>Phone (10 digits) *</label>
                                            <input
                                                type="text"
                                                name="phone"
                                                className="form-control"
                                                value={this.state.data.phone}
                                                onChange={this.handleDataChange}
                                                placeholder="1234567890"
                                            />
                                            {this.state.errors.phone &&
                                                <div className="text-danger">{this.state.errors.phone}</div>}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label>User Type *</label>
                                            {isEdit ? (
                                                // User Type is identity-defining — changing it would
                                                // orphan the user's company/role associations. Locked
                                                // on edit (read-only); only set at creation.
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={this.state.data.assignment_type === 'dealer' ? 'Dealer' : 'Installer'}
                                                    disabled
                                                    readOnly
                                                />
                                            ) : (
                                                <select
                                                    name="assignment_type"
                                                    className="form-control"
                                                    value={this.state.data.assignment_type}
                                                    onChange={this.handleAssignmentTypeChange}
                                                >
                                                    <option value="">Select User Type</option>
                                                    {ASSIGNMENT_TYPES.map(type => (
                                                        <option key={type.value} value={type.value}>
                                                            {type.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            )}
                                            {this.state.errors.assignment_type &&
                                                <div className="text-danger">{this.state.errors.assignment_type}</div>}
                                        </div>
                                    </div>
                                </div>

                                {/* Company + Role — both depend on the assignment type chosen above.
                                  * Role options come from getUnifiedRolesForType(company type) so a
                                  * dealer gets dealer roles (finance/reporting/contact…) and an
                                  * installer gets installer roles (owner/admin/manager/…).
                                  */}
                                {this.state.data.assignment_type && (
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label>
                                                    {this.state.data.assignment_type === 'dealer' ? 'Dealer Group *' : 'Installer *'}
                                                </label>
                                                <select
                                                    name="primary_company_id"
                                                    className="form-control"
                                                    value={String(this.state.data.primary_company_id)}
                                                    onChange={this.handleCompanySelectionChange}
                                                >
                                                    <option value="">Select...</option>
                                                    {this.state.data.assignment_type === 'dealer' &&
                                                        this.state.dealerGroupList.map(group => (
                                                            <option key={group.group_id || group.id} value={group.group_id || group.id}>
                                                                {group.group_name || group.name}
                                                            </option>
                                                        ))
                                                    }
                                                    {this.state.data.assignment_type === 'installer' &&
                                                        this.state.specialistList.map(specialist => (
                                                            <option key={specialist.Installer_id} value={specialist.Installer_id}>
                                                                {specialist.Installer_Name}
                                                            </option>
                                                        ))
                                                    }
                                                </select>
                                                {this.state.errors.primary_company_id &&
                                                    <div className="text-danger">{this.state.errors.primary_company_id}</div>}
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label>Role *</label>
                                                <select
                                                    name="role"
                                                    className="form-control"
                                                    value={this.state.data.role}
                                                    onChange={this.handleRoleChange}
                                                >
                                                    <option value="">Select Role</option>
                                                    {getUnifiedRolesForType(this.state.data.assignment_type).map(role => (
                                                        <option key={role.value} value={role.value}>
                                                            {role.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                {this.state.errors.role &&
                                                    <div className="text-danger">{this.state.errors.role}</div>}
                                                {this.state.data.role && (
                                                    <small className="text-muted">
                                                        {getUnifiedRolesForType(this.state.data.assignment_type).find(r => r.value === this.state.data.role)?.description}
                                                    </small>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Sub-scope picker: location_id on the user_companies row.
                                  * Empty = 0 = full access to every child. Pinned = location_id
                                  * (for installer) or dealer_id (for dealer). Technicians MUST
                                  * be pinned to a single location.
                                  */}
                                {this.state.data.assignment_type && this.state.data.primary_company_id && (
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label>
                                                    {this.state.data.assignment_type === 'dealer'
                                                        ? 'Select Dealership *'
                                                        : (this.state.data.role === 'technician' ? 'Select Installer Location *' : 'Scope to Installer Location (Optional)')}
                                                </label>
                                                <select
                                                    name="scoped_id"
                                                    className="form-control"
                                                    value={this.state.data.scoped_id || ''}
                                                    onChange={this.handleScopeLocationChange}
                                                >
                                                    <option value="">
                                                        {this.state.data.assignment_type === 'dealer' ? 'Select a dealership…' : 'All Installer Locations'}
                                                    </option>
                                                    {this.state.scopeLocations.map(location => (
                                                        <option key={location.id} value={location.id}>
                                                            {location.name}
                                                        </option>
                                                    ))}
                                                </select>
                                                {this.state.errors.scoped_id &&
                                                    <div className="text-danger">{this.state.errors.scoped_id}</div>}
                                                {this.state.data.assignment_type === 'installer' && this.state.data.role !== 'technician' && (
                                                    <small className="text-muted">
                                                        Leave empty for full access to every installer location
                                                    </small>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Actions */}
                                <div className="form-group">
                                    <button
                                        className="jv-btn jv-btn-primary"
                                        onClick={this.handleSave}
                                    >
                                        {isEdit ? 'Update User' : 'Create User'}
                                    </button>

                                    {isEdit && (
                                        <button
                                            className="jv-btn jv-btn-secondary"
                                            onClick={this.handlePasswordReset}
                                            disabled={!this.state.data.email}
                                        >
                                            Reset Password
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    render() {
        return (
            <div className="container-fluid">
                {this.state.viewMode === 'list' && this.renderUserList()}
                {(this.state.viewMode === 'create' || this.state.viewMode === 'edit') && this.renderUserForm()}
            </div>
        );
    }
}